import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from './services/api';
import {
  IconToday, IconWeek, IconList, IconPlus, IconFloorPlan,
  IconCapacity, IconAnalytics, IconWaitlist, IconSettings, IconLogout, IconTables,
} from './components/Icons';
import Menu from './components/Menu';
import Login from './components/Login';
import TodayView from './components/TodayView';
import WeekView from './components/WeekView';
import ReservationsList from './components/ReservationsList';
import ManualReservation from './components/ManualReservation';
import TableManagement from './components/TableManagement';
import FloorPlan from './components/FloorPlan';
import Analytics from './components/Analytics';
import Capacity from './components/Capacity';
import Waitlist from './components/Waitlist';
import LandingPage from './components/LandingPage';
import Apply from './components/Apply';
import ForgotDetails from './components/ForgotDetails';
import ResetPassword from './components/ResetPassword';
import ChangeUsername from './components/ChangeUsername';
import History from './components/History';
import CalendarView from './components/Calendar';
import Settings from './components/Settings';
import AdminPanel from './components/AdminPanel';
import Onboarding from './components/Onboarding';
import Inbox, { getUnreadCount, markInboxSeen } from './components/Inbox';
import { t, getLang, isRTL } from './i18n';

function IconInbox({ size = 15, strokeWidth = 1.6 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  );
}

function IconMenu({ size = 15, strokeWidth = 1.6 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18M3 12h18M3 18h18"/>
    </svg>
  );
}

function IconHistory({ size = 15, strokeWidth = 1.6 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/>
      <path d="M12 7v5l4 2"/>
    </svg>
  );
}

function IconCalendar({ size = 15, strokeWidth = 1.6 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  );
}

function IconLock({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
}

function IconLockOpen({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 9.9-1"/>
    </svg>
  );
}

const NAV = [
  { key: 'today',     labelKey: 'today',        Icon: IconToday     },
  { key: 'week',      labelKey: 'thisWeek',     Icon: IconWeek      },
  { key: 'calendar',  labelKey: 'calendar',     Icon: IconCalendar  },
  { key: 'list',      labelKey: 'reservations', Icon: IconList      },
  { key: 'history',   labelKey: 'history',      Icon: IconHistory   },
  { key: 'new',       labelKey: 'newBooking',   Icon: IconPlus      },
  { key: 'inbox',     labelKey: 'aiBookings',   Icon: IconInbox     },
  { key: 'floorplan', labelKey: 'floorPlan',    Icon: IconFloorPlan },
  { key: 'capacity',  labelKey: 'capacity',     Icon: IconCapacity  },
  { key: 'analytics', labelKey: 'analytics',    Icon: IconAnalytics },
  { key: 'waitlist',  labelKey: 'waitlist',     Icon: IconWaitlist  },
  { key: 'tables',    labelKey: 'tables',       Icon: IconTables    },
  { key: 'menu',      labelKey: 'menu',         Icon: IconMenu      },
  { key: 'settings',  labelKey: 'settings',     Icon: IconSettings  },
];

const NAV_GROUP_KEYS = [
  { labelKey: 'operations', keys: ['today','week','calendar','list','history','new','inbox'] },
  { labelKey: 'venue',      keys: ['floorplan','capacity'] },
  { labelKey: 'insights',   keys: ['analytics','waitlist'] },
  { labelKey: 'manage',     keys: ['tables','menu','settings'] },
];

export default function App() {
  const [authenticated, setAuthenticated]   = useState(null);
  const [isAdmin, setIsAdmin]               = useState(false);
  const [showLogin, setShowLogin]           = useState(false);
  const [showApply, setShowApply]           = useState(false);
  const [showForgot, setShowForgot]         = useState(false);
  const [activeTab, setActiveTab]           = useState('today');
  const [toasts, setToasts]                 = useState([]);
  const [restaurantName, setRestaurantName] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [allReservations, setAllReservations] = useState([]);
  const [unread, setUnread]                 = useState(0);
  const [unseenReservations, setUnseen]     = useState(0);
  const [unplacedTables, setUnplacedTables] = useState(0);
  const [lang, setLangState]                = useState(getLang());
  const wsRef = useRef(null);

  // Tab lock
  const [lockedTabs, setLockedTabs]         = useState([]);
  const [unlockedTabs, setUnlockedTabs]     = useState(new Set()); // tabs unlocked this session
  const [lockModal, setLockModal]           = useState(null); // { key } of tab trying to open
  const [lockPwd, setLockPwd]               = useState('');
  const [lockError, setLockError]           = useState('');
  const [lockChecking, setLockChecking]     = useState(false);
  const unlockTimers  = useRef(new Map());  // tabKey -> timeoutId
  const prevActiveTab = useRef(null);
  const LOCK_TIMEOUT  = 5 * 60 * 1000; // 5 minutes

  const T = (key) => t(key, lang);
  const rtl = isRTL(lang);

  useEffect(() => {
    api.me().then(d => {
      setAuthenticated(d.authenticated);
      setIsAdmin(!!d.isAdmin);
    }).catch(() => setAuthenticated(false));
  }, []);

  // Check if onboarding needed (no tables yet)
  useEffect(() => {
    if (!authenticated || isAdmin) return;
    api.getTables().then(tables => {
      if (tables.length === 0) setShowOnboarding(true);
    }).catch(() => {});
  }, [authenticated, isAdmin]);

  const fetchRestaurantName = useCallback(async () => {
    try { const s = await api.getSettings(); if (s.restaurant_name) setRestaurantName(s.restaurant_name); }
    catch (_) {}
  }, []);

  const fetchLockedTabs = useCallback(async () => {
    try {
      const s = await api.getSettings();
      const locked = Array.isArray(s.locked_tabs) ? s.locked_tabs : [];
      setLockedTabs(locked);
      // If the active tab just got locked, that's fine — it stays open for this session
    } catch (_) {}
  }, []);

  const fetchUnread = useCallback(async () => {
    try {
      const all = await api.getReservations({ source: 'ai', limit: 100 });
      setAllReservations(all);
      setUnread(getUnreadCount(all));
    } catch (_) {}
  }, []);

  const fetchUnplaced = useCallback(async () => {
    try {
      const { count } = await api.getUnplacedCount();
      setUnplacedTables(count);
    } catch (_) {}
  }, []);

  const fetchUnseen = useCallback(async () => {
    try {
      const { count } = await api.getUnseenCount();
      setUnseen(count);
    } catch (_) {}
  }, []);

  useEffect(() => { if (authenticated && !isAdmin) { fetchRestaurantName(); fetchUnread(); fetchUnplaced(); fetchUnseen(); fetchLockedTabs(); } }, [authenticated, isAdmin, fetchRestaurantName, fetchUnread, fetchUnplaced, fetchUnseen, fetchLockedTabs]);

  useEffect(() => {
    const handler = () => {
      fetchRestaurantName();
      fetchLockedTabs();
      // Clear all timers and re-lock everything
      unlockTimers.current.forEach(id => clearTimeout(id));
      unlockTimers.current.clear();
      setUnlockedTabs(new Set());
    };
    window.addEventListener('seva:settings-saved', handler);
    return () => window.removeEventListener('seva:settings-saved', handler);
  }, [fetchRestaurantName, fetchLockedTabs]);

  useEffect(() => {
    const handler = () => { fetchUnread(); fetchUnplaced(); fetchUnseen(); };
    window.addEventListener('seva:refresh', handler);
    return () => window.removeEventListener('seva:refresh', handler);
  }, [fetchUnread, fetchUnplaced, fetchUnseen]);

  useEffect(() => {
    window.addEventListener('seva:unseen-update', fetchUnseen);
    return () => window.removeEventListener('seva:unseen-update', fetchUnseen);
  }, [fetchUnseen]);

  // Auto-refresh badges every 30 seconds
  useEffect(() => {
    if (!authenticated || isAdmin) return;
    const interval = setInterval(() => { fetchUnread(); fetchUnseen(); }, 30000);
    return () => clearInterval(interval);
  }, [authenticated, isAdmin, fetchUnread, fetchUnseen]);

  const addToast = useCallback((msg, type = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    const apiUrl = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:3001`;
    const wsUrl = apiUrl.replace(/^https/, 'wss').replace(/^http/, 'ws');
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'NEW_RESERVATION') {
          const r = data.reservation;
          addToast(`${r.source === 'ai' ? 'AI booking' : 'New booking'} — ${r.customer_name}, ${r.party_size} guests at ${r.time_slot}`, r.source === 'ai' ? 'ai' : 'success');
          window.dispatchEvent(new CustomEvent('seva:refresh'));
          if (r.source === 'ai') { setUnread(u => u + 1); setUnseen(u => u + 1); }
        } else if (data.type === 'RESERVATION_CANCELLED') {
          addToast('A reservation was cancelled.', 'warning');
          window.dispatchEvent(new CustomEvent('seva:refresh'));
        } else if (data.type === 'RESERVATION_UPDATED') {
          window.dispatchEvent(new CustomEvent('seva:refresh'));
        }
      } catch (_) {}
    };
    return () => ws.close();
  }, [authenticated, addToast]);

  const relockTab = useCallback((key) => {
    if (unlockTimers.current.has(key)) {
      clearTimeout(unlockTimers.current.get(key));
      unlockTimers.current.delete(key);
    }
    setUnlockedTabs(prev => { const n = new Set(prev); n.delete(key); return n; });
  }, []);

  const startLockTimer = useCallback((key) => {
    if (unlockTimers.current.has(key)) clearTimeout(unlockTimers.current.get(key));
    const id = setTimeout(() => {
      unlockTimers.current.delete(key);
      setUnlockedTabs(prev => { const n = new Set(prev); n.delete(key); return n; });
    }, LOCK_TIMEOUT);
    unlockTimers.current.set(key, id);
  }, [LOCK_TIMEOUT]);

  // When active tab changes: pause timer on the tab being entered, resume on the one being left
  useEffect(() => {
    const prev = prevActiveTab.current;
    prevActiveTab.current = activeTab;
    // If leaving an unlocked tab, start its countdown
    if (prev && prev !== activeTab && unlockedTabs.has(prev)) {
      startLockTimer(prev);
    }
    // If entering an unlocked tab, pause the countdown (clear timer)
    if (unlockedTabs.has(activeTab)) {
      if (unlockTimers.current.has(activeTab)) {
        clearTimeout(unlockTimers.current.get(activeTab));
        unlockTimers.current.delete(activeTab);
      }
    }
  }, [activeTab, startLockTimer, unlockedTabs]);

  const handleLogin  = ({ isAdmin: admin }) => { setAuthenticated(true); setIsAdmin(!!admin); };
  const handleLogout = async () => { await api.logout(); setAuthenticated(false); setIsAdmin(false); };

  const handleTabChange = (key) => {
    if (lockedTabs.includes(key) && !unlockedTabs.has(key)) {
      setLockModal({ key });
      setLockPwd('');
      setLockError('');
      return;
    }
    setActiveTab(key);
    if (key === 'inbox') { markInboxSeen(); setUnread(0); }
  };

  const submitLockPwd = async () => {
    if (!lockModal) return;
    setLockChecking(true); setLockError('');
    try {
      const { valid } = await api.verifyOwnerPassword(lockPwd);
      if (valid) {
        const key = lockModal.key;
        setUnlockedTabs(prev => new Set([...prev, key]));
        // No timer yet — tab is being entered now, timer starts on leaving
        setLockModal(null);
        setLockPwd('');
        setActiveTab(key);
        if (key === 'inbox') { markInboxSeen(); setUnread(0); }
      } else {
        setLockError('Incorrect password');
      }
    } catch { setLockError('Could not verify password'); }
    finally { setLockChecking(false); }
  };

  // URL-based pages accessible without auth
  const path = window.location.pathname;
  if (path === '/reset-password') return <ResetPassword />;
  if (path === '/change-username') return <ChangeUsername />;

  if (authenticated === null) return (
    <div style={s.splash}>
      <div style={s.splashLogo}>
        <img src="/icon-192.png" alt="Seva" style={{ width: 32, height: 32, borderRadius: 8 }} />
      </div>
      <div style={s.splashText}>Loading…</div>
    </div>
  );
  if (!authenticated) {
    if (showForgot) return <ForgotDetails onBack={() => { setShowForgot(false); setShowLogin(true); }} />;
    if (showLogin) return <Login onLogin={handleLogin} onBack={() => setShowLogin(false)} onForgot={() => { setShowForgot(true); setShowLogin(false); }} />;
    if (showApply) return <Apply onBack={() => setShowApply(false)} onSignIn={() => { setShowApply(false); setShowLogin(true); }} />;
    return <LandingPage onSignIn={() => setShowLogin(true)} onApply={() => setShowApply(true)} />;
  }
  if (isAdmin) return <AdminPanel onLogout={handleLogout} />;

  const TABS = ['today','week','calendar','list','history','inbox','tables','menu','floorplan','capacity','analytics','waitlist','settings'];
  const tabComponent = (key) => {
    switch (key) {
      case 'today':     return <TodayView />;
      case 'week':      return <WeekView />;
      case 'list':      return <ReservationsList onNavigate={setActiveTab} />;
      case 'calendar':  return <CalendarView />;
      case 'history':   return <History />;
      case 'inbox':     return <Inbox />;
      case 'tables':    return <TableManagement />;
      case 'menu':      return <Menu />;
      case 'floorplan': return <FloorPlan isActive={activeTab === 'floorplan'} />;
      case 'capacity':  return <Capacity />;
      case 'analytics': return <Analytics />;
      case 'waitlist':  return <Waitlist />;
      case 'settings':  return <Settings />;
      default:          return null;
    }
  };

  return (
    <div style={{ ...s.app, direction: rtl ? 'rtl' : 'ltr' }}>
      {showOnboarding && (
        <Onboarding onComplete={() => {
          setLangState(getLang());
          setShowOnboarding(false);
          window.dispatchEvent(new CustomEvent('seva:refresh'));
        }} />
      )}

      <aside style={s.sidebar}>
        <div style={s.brand}>
          <div style={s.brandMark}>
            <img src="/icon-192.png" alt="Seva" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 9 }} />
          </div>
          <div>
            <div style={s.brandName}>{restaurantName || 'Seva'}</div>
            <div style={s.brandSub}>{T('management')}</div>
          </div>
        </div>

        <nav style={s.nav}>
          {NAV_GROUP_KEYS.map(group => (
            <div key={group.labelKey} style={s.navGroup}>
              <div style={s.navGroupLabel}>{T(group.labelKey)}</div>
              {group.keys.map(key => {
                const item     = NAV.find(n => n.key === key);
                const isActive = activeTab === key;
                return (
                  <button
                    key={key}
                    onClick={() => handleTabChange(key)}
                    style={{ ...s.navItem, ...(isActive ? s.navItemActive : {}) }}
                  >
                    <item.Icon size={15} strokeWidth={isActive ? 2 : 1.6} />
                    <span style={{ flex: 1 }}>{T(item.labelKey)}</span>
                    {lockedTabs.includes(key) && (
                      unlockedTabs.has(key) ? (
                        <span
                          style={s.lockOpenIcon}
                          title="Click to re-lock"
                          onClick={e => { e.stopPropagation(); relockTab(key); }}
                        >
                          <IconLockOpen size={10} />
                        </span>
                      ) : (
                        <span style={s.lockIcon}><IconLock size={10} /></span>
                      )
                    )}
                    {key === 'inbox' && unread > 0 && (
                      <span style={s.badge}>{unread > 99 ? '99+' : unread}</span>
                    )}
                    {key === 'list' && unseenReservations > 0 && (
                      <span style={{ ...s.badge, background: '#10b981' }}>{unseenReservations > 99 ? '99+' : unseenReservations}</span>
                    )}
                    {key === 'floorplan' && unplacedTables > 0 && (
                      <span style={{ ...s.badge, background: '#f59e0b' }} title={`${unplacedTables} table(s) not placed on floor plan`}>!</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <button onClick={handleLogout} style={s.logoutBtn}>
          <IconLogout size={14} />
          <span>{T('signOut')}</span>
        </button>
      </aside>

      <main style={s.main}>
        <div style={s.content}>
          {activeTab === 'new' && <ManualReservation onSaved={() => setActiveTab('list')} />}
          {TABS.map(key => (
            <div key={key} style={{ display: activeTab === key ? 'block' : 'none' }}>
              {tabComponent(key)}
            </div>
          ))}
        </div>
      </main>

      {lockModal && (
        <div style={s.lockOverlay} onClick={() => setLockModal(null)}>
          <div style={s.lockBox} onClick={e => e.stopPropagation()}>
            <div style={s.lockBoxIcon}>
              <IconLock size={22} />
            </div>
            <div style={s.lockBoxTitle}>Owner password required</div>
            <div style={s.lockBoxSub}>
              This tab is locked. Enter the owner password to continue.
            </div>
            {lockError && <div style={s.lockBoxErr}>{lockError}</div>}
            <input
              style={s.lockBoxInput}
              type="password"
              placeholder="Owner password"
              autoFocus
              autoComplete="new-password"
              value={lockPwd}
              onChange={e => { setLockPwd(e.target.value); setLockError(''); }}
              onKeyDown={e => { if (e.key === 'Enter') submitLockPwd(); }}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={s.lockBoxBtn} onClick={submitLockPwd} disabled={lockChecking}>
                {lockChecking ? 'Checking…' : 'Unlock'}
              </button>
              <button style={s.lockBoxCancel} onClick={() => setLockModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div style={s.toasts}>
        {toasts.map(t => (
          <div key={t.id} style={{ ...s.toast, ...s['t_' + t.type] }}>
            <div style={s.toastMsg}>{t.msg}</div>
            <button onClick={() => setToasts(p => p.filter(x => x.id !== t.id))} style={s.toastX}>×</button>
          </div>
        ))}
      </div>
    </div>
  );
}

const s = {
  splash:      { height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, background: '#f8fafc' },
  splashLogo:  { width: 56, height: 56, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(15,23,42,0.08)' },
  splashText:  { color: '#94a3b8', fontSize: 14 },

  app:         { display: 'flex', height: '100vh', overflow: 'hidden' },

  sidebar:     { width: 220, flexShrink: 0, background: '#0f172a', display: 'flex', flexDirection: 'column', borderRight: '1px solid rgba(255,255,255,0.04)' },

  brand:       { display: 'flex', alignItems: 'center', gap: 10, padding: '20px 16px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  brandMark:   { width: 34, height: 34, borderRadius: 9, overflow: 'hidden', flexShrink: 0 },
  brandName:   { fontSize: 14, fontWeight: 700, color: '#f1f5f9', letterSpacing: '-0.2px' },
  brandSub:    { fontSize: 11, color: 'rgba(255,255,255,0.25)', marginTop: 1 },

  nav:          { flex: 1, overflow: 'auto', padding: '12px 10px' },
  navGroup:     { marginBottom: 20 },
  navGroupLabel:{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '0 8px', marginBottom: 4 },
  navItem:      { display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '8px 10px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: 13, textAlign: 'left', borderRadius: 7, marginBottom: 1, transition: 'background 0.12s, color 0.12s' },
  navItemActive:{ background: 'rgba(255,255,255,0.08)', color: '#f1f5f9' },
  badge:        { background: '#4f46e5', color: '#fff', borderRadius: 10, fontSize: 10, fontWeight: 700, padding: '2px 6px', minWidth: 18, textAlign: 'center', lineHeight: '14px' },

  logoutBtn:   { display: 'flex', alignItems: 'center', gap: 9, margin: '0 10px 16px', padding: '8px 10px', background: 'none', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 7, color: 'rgba(255,255,255,0.25)', cursor: 'pointer', fontSize: 13 },

  main:        { flex: 1, overflow: 'auto', background: '#f8fafc' },
  content:     { padding: '32px 36px', maxWidth: 1400, margin: '0 auto' },

  lockIcon:     { display: 'flex', alignItems: 'center', opacity: 0.4 },
  lockOpenIcon: { display: 'flex', alignItems: 'center', opacity: 0.6, cursor: 'pointer', color: '#818cf8' },
  lockOverlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000 },
  lockBox:     { background: '#fff', borderRadius: 20, padding: '32px 36px', width: 360, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(15,23,42,0.2)', display: 'flex', flexDirection: 'column', gap: 14 },
  lockBoxIcon: { width: 48, height: 48, background: '#f1f5f9', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569' },
  lockBoxTitle:{ fontSize: 18, fontWeight: 800, color: '#0f172a', margin: 0 },
  lockBoxSub:  { fontSize: 13, color: '#64748b', lineHeight: 1.5, margin: 0 },
  lockBoxErr:  { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', fontSize: 13 },
  lockBoxInput:{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '11px 14px', fontSize: 14, outline: 'none', color: '#0f172a', width: '100%', boxSizing: 'border-box' },
  lockBoxBtn:  { flex: 1, background: '#4f46e5', border: 'none', borderRadius: 10, padding: '11px 0', fontSize: 14, fontWeight: 700, color: '#fff', cursor: 'pointer' },
  lockBoxCancel:{ flex: 1, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 10, padding: '11px 0', fontSize: 14, color: '#475569', cursor: 'pointer' },

  toasts:      { position: 'fixed', bottom: 24, right: 24, display: 'flex', flexDirection: 'column', gap: 8, zIndex: 9999, maxWidth: 360 },
  toast:       { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', borderRadius: 10, fontSize: 13, lineHeight: 1.4, background: '#1e293b', border: '1px solid rgba(255,255,255,0.06)', color: '#e2e8f0', boxShadow: '0 4px 16px rgba(0,0,0,0.2)' },
  toastMsg:    { flex: 1 },
  toastX:      { background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0, flexShrink: 0 },
  t_ai:        { borderLeft: '3px solid #818cf8' },
  t_success:   { borderLeft: '3px solid #34d399' },
  t_warning:   { borderLeft: '3px solid #fbbf24' },
  t_info:      { borderLeft: '3px solid #60a5fa' },
};
